<template>
  <div>
    <page-title
      title="It’s all about you"
      description="So we can start building your profile, it’s time for you to fill in a few personal and professional details."
    />

    <div class="flex items-center gap-x-6 mb-8 flex-wrap">
      <div class="w-full md:w-4/12 mb-0 md:mb-1.5">
        <h3 class="text-xl">Automatically import data from your resume <small class="text-gray-400">(optional)</small></h3>
      </div>

      <div class="flex-1">
        <label for="resume" class="p-6 rounded-lg border border-gray-400 border-dashed cursor-pointer block">
          <div class="flex items-center" :class="{ 'gap-x-4': files.length }">
            <input type="file" id="resume" @change="handleUpload" accept=".pdf" class="sr-only" />
            <div v-if="files.length" class="w-[60px] text-center">
              <img src="@/assets/icons/pdf.svg" alt="" class="w-[60px]" />
              <p class="whitespace-nowrap w-[60px] overflow-x-hidden">
                <small>{{ files[0]?.name }}</small>
              </p>
            </div>

            <div>
              <p class="text-lg">Drag & drop here or <span class="text-blue-700">Browse</span> to upload</p>
              <small class="text-gray-500">DOC, DOCX and PDF files of max 5 MB.</small>
            </div>
          </div>
        </label>
        <a :href="resumeUrl" target="_blank" class="font-medium text-lg underline inline-block mb-4 md:mb-0 mt-1.5" v-if="resumeUrl"
          >View previously uploaded resume</a
        >
      </div>
    </div>

    <div class="flex items-center gap-x-6 mb-8 flex-wrap">
      <div class="w-full md:w-4/12 mb-0 md:mb-1.5">
        <h3 class="text-xl">Upload your profile picture</h3>
      </div>

      <label for="photoFile" class="flex-1 p-6 rounded-lg border border-gray-400 border-dashed cursor-pointer">
        <div class="flex items-center">
          <input type="file" id="photoFile" @change="handlePhotoUpload" accept=".jpg,.jpeg,.png" class="sr-only" />
          <div v-show="filesPhoto.length || avatarUrl" class="w-[80px] mr-3 text-center">
            <img :src="avatarUrl" id="photo" alt="" class="w-[80px] rounded-lg shadow border-2 border-double" />
          </div>

          <div>
            <p class="text-lg">Drag & drop here or <span class="text-blue-700">Browse</span> to upload</p>
            <small class="text-gray-500">Min 200px X 200px. JPG or PNG files of max 2MB.</small>
          </div>
        </div>
      </label>
    </div>

    <h2 class="mb-4 font-semibold text-xl">Your details</h2>

    <div class="mb-6">
      <div>
        <Input type="text" label="Full name" placeholder="Enter your full name" v-model="details.candidate_name" />
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      <div class="">
        <label class="block mb-1.5 text-lg font-albert text-brand-black/70">Phone Number</label>
        <div class="flex items-start gap-x-3">
          <Select id="dial_code" :options="dialCodes" place-holder="Select code" v-model="dialcode" class="w-[130px]" />
          <Input type="text" class="flex-1" placeholder="Phone number" v-model="details.candidate_phone" />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-6">
        <div>
          <label for="" class="block text-md font-albert text-brand-black/70 mb-2">Date of Birth</label>
          <VueDatePicker format="MM/dd/yyyy" v-model="details.dob" placeholder="yyyy-mm-dd"></VueDatePicker>
        </div>

        <Select
          id="gender"
          label="Gender (optional)"
          :options="[
            { key: 'male', value: 'Male' },
            { key: 'female', value: 'Female' },
          ]"
          place-holder="Select"
          v-model="details.gender"
        />
      </div>

      <div>
        <Input type="text" label="Role" placeholder="Role title" v-model="details.role_title" />
      </div>

      <div>
        <Input type="number" min="1" label="Years of Experience" placeholder="Years of Experience" v-model="details.years_of_experience" />
      </div>
    </div>

    <h2 class="mb-4 font-semibold text-xl">Your location <small class="text-gray-400">(required)</small></h2>
    <div class="grid grid-cols-2 gap-6 mb-6">
      <div>
        <Input type="text" label="City" placeholder="City" v-model="details.city" />
      </div>

      <div>
        <Input type="text" label="State" placeholder="State" v-model="details.state" />
      </div>

      <div>
        <Select id="country" label="Country" :options="countryList" place-holder="Select" v-model="details.country" />
      </div>

      <div>
        <Select id="timezones" label="Timezone" :options="timezoneList" place-holder="Select" v-model="details.timezone" />
      </div>
    </div>

    <h2 class="mb-4 font-semibold text-xl">Social Profiles <small class="text-gray-400">(optional)</small></h2>
    <div class="grid grid-cols-2 gap-6 mb-6">
      <div>
        <Input type="url" label="Linkedin" placeholder="https://linkedin.com/in/user" v-model="details.social_links.linkedin" />
      </div>

      <div>
        <Input type="url" label="Personal website" placeholder="https://" v-model="details.social_links.website" />
      </div>

      <div>
        <Input type="url" label="Github" placeholder="https://github.com/user" v-model="details.social_links.github" />
      </div>

      <div>
        <Input type="url" label="Stack overflow" placeholder="https://" v-model="details.social_links.stackoverflow" />
      </div>

      <div>
        <Input type="url" label="Behance" placeholder="https://" v-model="details.social_links.behance" />
      </div>

      <div>
        <Input type="url" label="Dribbble" placeholder="https://" v-model="details.social_links.dribbble" />
      </div>

      <div>
        <Input type="url" label="Glassdoor profile url" placeholder="https://" v-model="details.social_links.glassdoor" />
      </div>
    </div>
  </div>

  <loading-overlay :is-loading="loading" :text="loadingTxt" />
</template>

<script setup>
import PageTitle from './PageTitle.vue';
import Input from '@/components/form/Input.vue';
import Select from '../form/Select.vue';
import { computed, ref } from 'vue';
import { timezones } from '@/data/timezones';
import LoadingOverlay from '../Loading.vue';
import { useStore } from 'vuex';
import { alert, errorMessage } from '@/utils/helper';

const props = defineProps({
  details: {
    type: Object,
  },
  countries: {
    type: Array,
  },
  timezones: {
    type: Array,
  },
  resumeUrl: {
    type: String,
    required: false,
  },
  avatarUrl: {
    type: String,
    required: false,
  },
});

const store = useStore();
const dialcode = ref('');
const resumeDetails = ref(null);
const parseResumeLink = ref('');
const parsed_resume = ref(null);
const loading = ref(false);
const loadingTxt = ref('');
const files = ref([]);
const filesPhoto = ref([]);
const emit = defineEmits(['done', 'photo-uploaded']);
const error = ref(false);

const dialCodes = computed(() => {
  return props.countries.map((item) => ({ value: `${item.name} ${item.dial_code}`, key: item.dial_code }));
});

const countryList = computed(() => {
  return props.countries.map((item) => ({ value: `${item.name}`, key: item.code }));
});

const timezoneList = computed(() => {
  return props.timezones.map((item) => ({ value: `${item.text}`, key: item.abbr }));
});

const handleUpload = async (e) => {
  if (!e) return;

  files.value = Array.from(e.target.files) || [];

  if (files.value[0]?.type != 'application/pdf') {
    return alert('Invalid file type. Upload a PDF file', 'error');
  }

  if (files.value[0].size > 10000000) {
    error.value = true;
  }
  resumeDetails.value = files.value[0];

  await uploadResume();
};

const handlePhotoUpload = async (e) => {
  if (!e) return;

  filesPhoto.value = Array.from(e.target.files) || [];
  const fileType = filesPhoto.value[0]?.type;

  if (!['image/jpg', 'image/png', 'image/jpeg'].includes(fileType)) {
    return alert('Invalid file type. Upload a png or jpg file', 'error');
  }

  if (filesPhoto.value[0].size > 10000000) {
    error.value = true;
  }

  const output = document.querySelector('#photo');
  output.src = URL.createObjectURL(e.target.files[0]);
  emit('photo-uploaded', filesPhoto.value[0]);
};

function clearFile() {
  files.value = [];
  error.value = false;
}

const parseResumeInterval = setInterval(() => parseResume(), 20000);

const uploadResume = async () => {
  loading.value = true;
  loadingTxt.value = 'Parsing resume, please wait..';
  try {
    // Upload resume and get file url
    const data = new FormData();
    data.append('resume', resumeDetails.value);
    const { data: rData } = await store.dispatch('auth/onboardResume', data);

    // Use file url to parse resume
    const parsedData = await store.dispatch('global/parseResumeCB', { resume_url: rData?.resume_link });
    const { certifications, educations, email, name, phone, projects, skills, work_experiences, languages } = parsedData?.data;

    const details = {
      candidate_name: name,
      candidate_email: email,
      candidate_phone: phone,
      candidate_spoken_languages: languages,
      candidate_courses_and_certifications: certifications,
      positions: work_experiences.map(({ company, location, role, responsibilities, started_at, ended_at }) => {
        return {
          position_name: role,
          company_name: `${company}, ${location}`,
          job_details: responsibilities.join(', '),
          start_date: started_at,
          end_date: ended_at,
        };
      }),
      education_qualifications: educations.map(({ degree, institution, year }) => {
        return { degree_type: degree, school_name: institution, end_date: year };
      }),
      skills,
      projects: projects.map(({ title, details, website }) => {
        return { title, details: details.join(', '), description: website };
      }),
    };
    emit('done', { parsedResume: details, resumeFile: resumeDetails.value });
  } catch (error) {
    console.log(error)
    alert(errorMessage(error), 'error');
  } finally {
    loading.value = false;
  }
};

const parseResume = async () => {
  if (!parseResumeLink.value) return;
  loading.value = true;
  try {
    // parse sharpapi uploaded resume
    const parseResult = await store.dispatch('global/parseResumeSharpApi', parseResumeLink.value);
    const parsedDetails = parseResult?.data?.attributes?.result;

    if (parsedDetails) {
      parsed_resume.value = parsedDetails;
      emit('done', { parsedResume: parsedDetails, resumeFile: resumeDetails.value });
      loading.value = false;
      loadingTxt.value = '';

      for (var i = 1; i < 99999; i++) window.clearInterval(i); // clear interval
    } else {
      loading.value = true;
    }
  } catch (error) {
    alert(errorMessage(error), 'error');
  } finally {
    // loading.value = false;
  }
};
</script>
